import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Card,
  Typography,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import { Delete, VolumeUp, Reorder } from '@mui/icons-material';

interface VideoEntry {
  vid: string;
  title: string;
  filesize?: number | null;
  duration?: number | null;
  resolution?: string | null;
  rank: number;
  index?: number; // Current playback index (passed from MusicPlayer)
}

interface DraggableVideoListProps {
  playlist: VideoEntry[];
  playlistId: string;
  currentVid?: string;
  onRemove: (vid: string, reorderedPlaylist?: VideoEntry[]) => void;
  onPlay: (vid: string) => void;
}

const formatTime = (seconds: number | null | undefined): string => {
  if (!seconds || isNaN(seconds)) return "N/A";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

interface SortableVideoCardProps {
  video: VideoEntry;
  onRemove: (vid: string) => void;
  onPlay: (vid: string) => void;
}

const SortableVideoCard: React.FC<SortableVideoCardProps> = ({
  video,
  onRemove,
  onPlay,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.vid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  const theme = useTheme();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const isCurrentlyPlaying = video.index === 0;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: isDragging ? 'grabbing' : 'pointer',
        flexDirection: 'row',
        width: '100%',
        transition: 'all 0.2s ease-out',
        ...(isCurrentlyPlaying && {
          border: '2px solid #1db954',
          backgroundColor: 'rgba(29, 185, 84, 0.08)',
          boxShadow: '0 0 15px rgba(29, 185, 84, 0.4)',
        }),
        ...(isDragging && {
          boxShadow: '0 12px 24px rgba(0,0,0,0.5)',
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          transform: 'scale(1.02)',
        }),
      }}
      onClick={() => onPlay(video.vid)}
    >
      {/* Drag Handle */}
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        sx={{
          mr: 1,
          color: isDragging ? '#1db954' : 'text.disabled',
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          WebkitTouchCallout: 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            color: '#1db954',
            backgroundColor: 'transparent',
          },
        }}
      >
        <Reorder />
      </IconButton>

      {/* Thumbnail */}
      <Box
        sx={{
          position: 'relative',
          flex: '0 0 auto',
          minWidth: { xs: 120, sm: 200 },
          width: { xs: 120, sm: 200 },
          height: { xs: 68, sm: 112 },
          overflow: 'hidden',
          borderRadius: 0.5,
          mr: { xs: 1, sm: 2 },
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={`${backendUrl}/api/thumbnail/${video.vid}`}
          alt={video.title}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />

        {/* Now Playing Mask */}
        {isCurrentlyPlaying && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(29, 185, 84, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <VolumeUp
              sx={{
                color: 'white',
                fontSize: { xs: 24, sm: 32 },
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                fontSize: { xs: 10, sm: 12 },
              }}
            >
              NOW PLAYING
            </Typography>
          </Box>
        )}

        {/* Duration Badge */}
        {video.duration !== undefined && video.duration !== null && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 3,
              bgcolor: 'rgba(0,0,0,0.3)',
              color: 'white',
              px: 0.6,
              py: 0.3,
              borderRadius: 0.5,
              fontSize: 12,
            }}
          >
            {formatTime(video.duration)}
          </Box>
        )}
      </Box>

      {/* Video Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...(isCurrentlyPlaying && {
              color: '#1db954',
              fontWeight: 'bold',
            }),
          }}
        >
          {video.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {video.filesize ? `${(video.filesize / 1048576).toFixed(2)} MB` : ''} |{' '}
          {video.resolution || 'N/A'}
        </Typography>
      </Box>

      {/* Delete Button */}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(video.vid);
        }}
        sx={{
          color: 'text.secondary',
          '&:hover': { color: 'error.main' },
        }}
      >
        <Delete fontSize="small" />
      </IconButton>
    </Card>
  );
};

const DraggableVideoList: React.FC<DraggableVideoListProps> = ({
  playlist,
  playlistId,
  currentVid,
  onRemove,
  onPlay,
}) => {
  const [activeVideo, setActiveVideo] = useState<VideoEntry | null>(null);
  const theme = useTheme();

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,      // feels good on iOS (not too fast, not too slow)
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const video = playlist.find((v) => v.vid === event.active.id);
    if (video) {
      setActiveVideo(video);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveVideo(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = playlist.findIndex((v) => v.vid === active.id);
    const newIndex = playlist.findIndex((v) => v.vid === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Create the reordered list immediately for optimistic update
    const newVideos = arrayMove(playlist, oldIndex, newIndex);

    // Update ranks based on new order (1-indexed)
    const reorderedVideos = newVideos.map((video, index) => ({
      ...video,
      rank: index + 1,
    }));

    // Pass the reordered data to parent for IMMEDIATE optimistic UI update
    // The parent will handle the API call and error handling
    onRemove('REORDER_' + active.id, reorderedVideos);
  };

  const handleDragCancel = () => {
    setActiveVideo(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={playlist.map((v) => v.vid)}
        strategy={verticalListSortingStrategy}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1,
            transition: 'all 0.2s ease-out'
          }}
        >
          {playlist.map((video) => (
            <SortableVideoCard
              key={video.vid}
              video={{ ...video, index: playlist.findIndex((v) => v.vid === currentVid) === playlist.indexOf(video) ? 0 : undefined }}
              onRemove={onRemove}
              onPlay={onPlay}
            />
          ))}
        </Box>
      </SortableContext>

      <DragOverlay>
        {activeVideo ? (
          <Card
            sx={{
              pointerEvents: 'none',
              opacity: 0.95,
              backgroundColor: alpha(theme.palette.background.paper, 0.98),
              backdropFilter: 'blur(20px)',
              border: '2px solid #1db954',
              boxShadow: '0 20px 40px rgba(0,0,0,0.6), 0 0 20px rgba(29, 185, 84, 0.5)',
            }}
          >
            <SortableVideoCard
              video={activeVideo}
              onRemove={() => {}}
              onPlay={() => {}}
            />
          </Card>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default DraggableVideoList;
